import React from 'react';
import { Box, Typography, Grid, Paper, Button, Chip, Stack, Divider, Container } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const tierOrder = ['Follower', 'Supporter', 'Hacker'] as const;
const tierEmoji: Record<string, string> = { Follower: '🌸', Supporter: '💜', Hacker: '🔐' };
const tierColors: Record<string, string> = { Follower: '#c97cff', Supporter: '#b44fff', Hacker: '#ff6eb4' };
const tierPrices: Record<string, string> = { Follower: '$5 / mo', Supporter: '$12 / mo', Hacker: '$25 / mo' };
const tierPerks: Record<string, string[]> = {
  Follower: [
    'Exclusive Patreon posts',
    'Early stream announcements',
    'Discord community access',
    'Monthly wallpaper pack',
  ],
  Supporter: [
    'Everything in Follower',
    'Monthly digital download',
    'WIP previews & behind-the-scenes',
    'Vote on future projects',
    'Name in stream credits',
  ],
  Hacker: [
    'Everything in Supporter',
    'Full project source files',
    'Hardware hacking posts',
    'Monthly 1:1 Q&A session',
    'Private Discord channel',
    'Physical reward (quarterly)',
  ],
};

const PATREON  = 'https://patreon.com/panekopanko';
const BMAC     = 'https://buymeacoffee.com/panekopanko';
const VGEN     = 'https://vgen.co/panekopanko';

const physical = [
  {
    emoji: '🏷️',
    title: 'Sticker Set',
    price: '$15',
    color: '#ff6eb4',
    desc: 'Die-cut vinyl sticker set. Waterproof and durable.',
    tags: ['Vinyl', 'Waterproof', 'Physical'],
  },
  {
    emoji: '🔑',
    title: 'Hacker Girl Keychain',
    price: '$15',
    color: '#c97cff',
    desc: 'Acrylic double-sided keychain.',
    tags: ['Acrylic', 'Physical'],
  },
  {
    emoji: '🐾',
    title: 'Pwnagotchi Prebuilt',
    price: '$75',
    color: '#b44fff',
    desc: 'Ready-to-use Pwnagotchi — flashed, configured, and good to go. Just add a battery.',
    tags: ['Hardware', 'WiFi', 'Physical'],
  },
];

export default function ShopPage() {
  return (
    <Box sx={{ background: '#0d0a12', minHeight: '100vh', pt: 10, pb: 16 }}>
      {/* Hero */}
      <Box sx={{
        py: { xs: 8, md: 10 },
        px: { xs: 3, md: 8 },
        background: 'radial-gradient(ellipse 70% 50% at 60% 0%, rgba(255,110,180,0.12) 0%, transparent 70%), #0d0a12',
        borderBottom: '1px solid rgba(255,110,180,0.1)',
      }}>
        <Container maxWidth="lg">
          <Button
            component={RouterLink}
            to="/"
            startIcon={<ArrowBackIcon />}
            sx={{ color: 'text.secondary', mb: 4, '&:hover': { color: 'primary.main' } }}
          >
            Home
          </Button>
          <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 6, fontSize: '0.75rem', display: 'block' }}>
            SHOP
          </Typography>
          <Typography variant="h1" sx={{ fontSize: { xs: '2.5rem', md: '4rem' }, lineHeight: 1.1, mt: 1, mb: 2 }}>
            Merch & Digital
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '1rem', lineHeight: 1.8, maxWidth: 520 }}>
            Physical items available on Patreon and Buy Me a Coffee. Digital products on VGen.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ pt: { xs: 8, md: 12 } }}>

        {/* Physical products */}
        <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 5, fontSize: '0.75rem' }}>
          PHYSICAL
        </Typography>
        <Typography variant="h3" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, mt: 1, mb: 2 }}>
          Merch
        </Typography>
        <Typography sx={{ color: 'text.secondary', mb: 6, fontSize: '0.95rem' }}>
          Order via Patreon or Buy Me a Coffee.
        </Typography>

        <Grid container spacing={3} sx={{ mb: 12 }}>
          {physical.map((item) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.title}>
              <Paper sx={{
                height: '100%',
                overflow: 'hidden',
                background: 'rgba(22,15,32,0.9)',
                border: `1px solid ${item.color}22`,
                transition: 'all 0.3s',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  borderColor: `${item.color}66`,
                  transform: 'translateY(-6px)',
                  boxShadow: `0 12px 40px ${item.color}20`,
                },
              }}>
                {/* Card image area */}
                <Box sx={{
                  height: 160,
                  background: `linear-gradient(135deg, ${item.color}18, ${item.color}08)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  position: 'relative',
                  flexShrink: 0,
                }}>
                  <Box sx={{
                    position: 'absolute', inset: 0,
                    backgroundImage: `radial-gradient(${item.color}15 1px, transparent 1px)`,
                    backgroundSize: '18px 18px',
                  }} />
                  <Typography sx={{
                    fontSize: '3.5rem', position: 'relative', zIndex: 1,
                    filter: `drop-shadow(0 0 20px ${item.color}99)`,
                  }}>
                    {item.emoji}
                  </Typography>
                </Box>

                <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.3 }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ color: item.color, fontWeight: 800, fontSize: '1.1rem', ml: 1, whiteSpace: 'nowrap' }}>
                      {item.price}
                    </Typography>
                  </Stack>

                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 2 }}>
                    {item.desc}
                  </Typography>

                  <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.8, mb: 3 }}>
                    {item.tags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" sx={{
                        fontSize: '0.65rem',
                        background: `${item.color}12`,
                        border: `1px solid ${item.color}33`,
                        color: item.color,
                      }} />
                    ))}
                  </Stack>

                  <Stack spacing={1.5} sx={{ mt: 'auto' }}>
                    <Button
                      fullWidth
                      variant="contained"
                      href={PATREON}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<OpenInNewIcon sx={{ fontSize: '13px !important' }} />}
                      startIcon={<LocalShippingIcon sx={{ fontSize: '16px !important' }} />}
                      sx={{
                        background: `linear-gradient(135deg, ${item.color}, ${item.color}bb)`,
                        '&:hover': { boxShadow: `0 0 20px ${item.color}44` },
                      }}
                    >
                      Buy on Patreon
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      href={BMAC}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<OpenInNewIcon sx={{ fontSize: '13px !important' }} />}
                      sx={{
                        borderColor: `${item.color}44`, color: item.color,
                        '&:hover': { borderColor: item.color, background: `${item.color}10` },
                      }}
                    >
                      Buy Me a Coffee
                    </Button>
                  </Stack>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ borderColor: 'rgba(255,110,180,0.1)', mb: 12 }} />

        {/* Patreon */}
        <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 5, fontSize: '0.75rem' }}>
          MEMBERSHIP
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-end' }} sx={{ mt: 1, mb: 2 }}>
          <Typography variant="h3" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
            Patreon
          </Typography>
          <Button
            variant="contained"
            href={PATREON}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<OpenInNewIcon />}
            sx={{
              background: 'linear-gradient(135deg, #ff424d, #e6461a)',
              fontWeight: 700, px: 3, py: 1.2,
              '&:hover': { boxShadow: '0 0 24px rgba(255,66,77,0.45)' },
            }}
          >
            Join on Patreon
          </Button>
        </Stack>
        <Typography sx={{ color: 'text.secondary', mb: 6, fontSize: '0.95rem', maxWidth: 560 }}>
          Support the work and get access to behind-the-scenes content, digital downloads, source files, and physical rewards.
        </Typography>

        <Grid container spacing={3} sx={{ mb: 12 }}>
          {tierOrder.map((tier, i) => (
            <Grid size={{ xs: 12, md: 4 }} key={tier}>
              <Paper sx={{
                p: 4, height: '100%',
                background: i === 1
                  ? `linear-gradient(135deg, ${tierColors[tier]}18, ${tierColors[tier]}08)`
                  : 'rgba(22,15,32,0.9)',
                border: i === 1
                  ? `2px solid ${tierColors[tier]}77`
                  : `1px solid ${tierColors[tier]}25`,
                position: 'relative',
                transition: 'all 0.3s',
                display: 'flex', flexDirection: 'column',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 12px 40px ${tierColors[tier]}20` },
              }}>
                {i === 1 && (
                  <Chip label="Most Popular" size="small" sx={{
                    position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                    background: `linear-gradient(135deg, ${tierColors[tier]}, ${tierColors[tier]}bb)`,
                    color: '#fff', fontWeight: 700,
                  }} />
                )}
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: '1.8rem' }}>{tierEmoji[tier]}</Typography>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>{tier}</Typography>
                    <Typography variant="h5" sx={{ color: tierColors[tier], fontWeight: 800 }}>{tierPrices[tier]}</Typography>
                  </Box>
                </Stack>
                <Divider sx={{ borderColor: `${tierColors[tier]}20`, my: 2 }} />
                <Stack spacing={1.2} sx={{ mb: 3, flex: 1 }}>
                  {tierPerks[tier].map((perk) => (
                    <Stack key={perk} direction="row" spacing={1} alignItems="flex-start">
                      <CheckCircleOutlineIcon sx={{ color: tierColors[tier], fontSize: 16, mt: '2px', flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>{perk}</Typography>
                    </Stack>
                  ))}
                </Stack>
                <Button
                  fullWidth
                  variant={i === 1 ? 'contained' : 'outlined'}
                  href={PATREON}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
                  sx={i === 1 ? {
                    background: `linear-gradient(135deg, ${tierColors[tier]}, ${tierColors[tier]}cc)`,
                    '&:hover': { boxShadow: `0 0 20px ${tierColors[tier]}44` },
                  } : {
                    borderColor: `${tierColors[tier]}44`, color: tierColors[tier],
                    '&:hover': { borderColor: tierColors[tier], background: `${tierColors[tier]}10` },
                  }}
                >
                  Join as {tier}
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ borderColor: 'rgba(255,110,180,0.1)', mb: 12 }} />

        {/* Digital products */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 5, fontSize: '0.75rem' }}>
              DIGITAL
            </Typography>
            <Typography variant="h3" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, mt: 1, mb: 2 }}>
              Digital Products
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.95rem', lineHeight: 1.8, maxWidth: 480 }}>
              Digital commissions and downloadable products are available through my VGen store — overlays, avatar assets, and more.
            </Typography>
          </Box>
          <Paper sx={{
            p: 4, flexShrink: 0, width: { xs: '100%', md: 320 },
            background: 'linear-gradient(135deg, rgba(180,79,255,0.12), rgba(255,110,180,0.08))',
            border: '1px solid rgba(180,79,255,0.3)',
            textAlign: 'center',
          }}>
            <Typography sx={{ fontSize: '3rem', mb: 1, filter: 'drop-shadow(0 0 20px rgba(180,79,255,0.6))' }}>
              🛒
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>VGen Store</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              Browse and purchase digital products directly.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              href={VGEN}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNewIcon />}
              startIcon={<DownloadIcon />}
              sx={{
                background: 'linear-gradient(135deg, #b44fff, #ff6eb4)',
                '&:hover': { boxShadow: '0 0 24px rgba(180,79,255,0.4)' },
              }}
            >
              Browse VGen Store
            </Button>
          </Paper>
        </Stack>

      </Container>
    </Box>
  );
}
